import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Upload, FileText, X, CheckCircle, AlertCircle, Brain, Eye, ChevronDown, ChevronRight, MapPin } from 'lucide-react';
import { uploadFile, analyzeFactSheet, getAnalysis, createProject, associateFactSheetWithProject, deleteFactSheet, getAuthenticatedFileUrl, VWBAAnalysis, matchProject, Project, updateProject } from '../services/api';
import VWBALoader from './VWBALoader';
import DocxPreview from './DocxPreview';
import FactsheetComparisonModal from './FactsheetComparisonModal';

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  status: 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
  factSheetId?: string;
  projectName?: string;
  analysisStatus?: 'pending' | 'analyzing' | 'completed' | 'error';
  vwbaAnalysis?: VWBAAnalysis;
  rawResponse?: string;
  filename?: string; // The actual filename stored on disk
  authenticatedUrl?: string; // Authenticated blob URL for file access
  matchedProject?: Project; // Existing project that matches this file
}

const UploadPage: React.FC = () => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [viewMode, setViewMode] = useState<'structured' | 'raw'>('structured');
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [showDetails, setShowDetails] = useState<Record<string, boolean>>({});
  const [showLoader, setShowLoader] = useState(false);
  const loaderStartedAt = useRef<number>(0);
  const MINIMUM_LOADER_MS = 8000; // Show loader for at least 8 seconds

  const hideLoader = useCallback(() => {
    const elapsed = Date.now() - loaderStartedAt.current;
    const remaining = MINIMUM_LOADER_MS - elapsed;
    if (remaining > 0) {
      setTimeout(() => setShowLoader(false), remaining);
    } else {
      setShowLoader(false);
    }
  }, []);
  const [comparisonModal, setComparisonModal] = useState<{
    isOpen: boolean;
    extractedData: VWBAAnalysis | null;
    existingProject: Project | null;
    factSheetId: string;
    fileId: string;
  } | null>(null);
  const [manualLocations, setManualLocations] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Auto-dismiss toast after 4 seconds
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Cleanup blob URLs on component unmount
  useEffect(() => {
    return () => {
      uploadedFiles.forEach(file => {
        if (file.authenticatedUrl) {
          URL.revokeObjectURL(file.authenticatedUrl);
        }
      });
    };
  }, []);

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    
    // Check file sizes (max 50MB)
    const MAX_FILE_SIZE = 50 * 1024 * 1024;
    const validFiles = fileArray.filter(file => file.size <= MAX_FILE_SIZE);
    
    if (validFiles.length === 0) {
      alert('No valid files to upload. Maximum file size is 50MB.');
      return;
    }

    // Create initial file states
    const newFiles: UploadedFile[] = validFiles.map((file, index) => ({
      id: `${Date.now()}-${index}`,
      name: file.name,
      size: file.size,
      status: 'uploading',
      progress: 0,
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);

    // Upload files
    for (const file of validFiles) {
      try {
        const fileId = newFiles.find(f => f.name === file.name)?.id;
        if (!fileId) continue;

        // Update progress
        setUploadedFiles(prev => 
          prev.map(f => f.id === fileId ? { ...f, progress: 50 } : f)
        );

        const result = await uploadFile(file);
        
        if (result.success && result.factSheet) {
          // Generate authenticated URL for the file
          let authenticatedUrl: string | undefined;
          try {
            authenticatedUrl = await getAuthenticatedFileUrl(result.factSheet.filename);
          } catch (error) {
            console.error('Failed to generate authenticated URL:', error);
          }
          
          setUploadedFiles(prev => 
            prev.map(f => f.id === fileId ? { 
              ...f, 
              status: 'success', 
              progress: 100,
              factSheetId: result.factSheet!.id,
              analysisStatus: 'pending',
              filename: result.factSheet!.filename,
              authenticatedUrl
            } : f)
          );
          
          // Automatically start analysis after successful upload
          if (result.factSheet.id) {
            startAnalysis(result.factSheet.id, fileId);
          }
        } else {
          setUploadedFiles(prev => 
            prev.map(f => f.id === fileId ? { 
              ...f, 
              status: 'error', 
              error: result.error || 'Upload failed'
            } : f)
          );
        }
      } catch (error) {
        const fileId = newFiles.find(f => f.name === file.name)?.id;
        if (fileId) {
          setUploadedFiles(prev => 
            prev.map(f => f.id === fileId ? { 
              ...f, 
              status: 'error', 
              error: error instanceof Error ? error.message : 'Upload failed'
            } : f)
          );
        }
      }
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  }, [handleFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  }, [handleFiles]);

  const startAnalysis = async (factSheetId: string, fileId: string) => {
    try {
      loaderStartedAt.current = Date.now();
      setShowLoader(true);

      setUploadedFiles(prev => 
        prev.map(f => f.id === fileId ? { 
          ...f, 
          analysisStatus: 'analyzing' 
        } : f)
      );

      // Start the analysis (this will return immediately)
      const result = await analyzeFactSheet(factSheetId);
      
      if (result.success) {
        // Start polling for results
        pollAnalysisStatus(factSheetId, fileId);
      } else {
        hideLoader();
        setUploadedFiles(prev =>
          prev.map(f => f.id === fileId ? {
            ...f,
            analysisStatus: 'error',
            error: result.error || 'Analysis failed'
          } : f)
        );
      }
    } catch (error) {
      hideLoader();
      setUploadedFiles(prev =>
        prev.map(f => f.id === fileId ? {
          ...f,
          analysisStatus: 'error',
          error: error instanceof Error ? error.message : 'Analysis failed'
        } : f)
      );
    }
  };

  const pollAnalysisStatus = async (factSheetId: string, fileId: string) => {
    const pollInterval = 2000; // Poll every 2 seconds
    const maxAttempts = 150; // Max 5 minutes of polling
    let attempts = 0;

    const poll = async () => {
      try {
        attempts++;
        const result = await getAnalysis(factSheetId);
        
        if (result.success && result.analysis) {
          const analysis = result.analysis;
          
          if (analysis.status === 'COMPLETED') {
            // Parse the results
            const vwbaAnalysis = analysis.results ? JSON.parse(analysis.results) : null;

            // Check for matching project
            if (vwbaAnalysis) {
              const projectName = vwbaAnalysis.projectName || '';
              const projectBasin = vwbaAnalysis.extractedData?.basin || vwbaAnalysis.extractedData?.location || '';

              if (projectName && projectBasin) {
                try {
                  const matchResult = await matchProject(projectName, projectBasin);

                  if (matchResult.match && matchResult.project) {
                    // Found a matching project - store on file for inline display
                    hideLoader();
                    setUploadedFiles(prev =>
                      prev.map(f => f.id === fileId ? {
                        ...f,
                        analysisStatus: 'completed',
                        vwbaAnalysis: vwbaAnalysis,
                        rawResponse: analysis.rawResponse,
                        matchedProject: matchResult.project!,
                      } : f)
                    );
                    return; // Stop polling
                  }
                } catch (matchError) {
                  console.error('Error checking for matching project:', matchError);
                  // Continue with normal flow if matching fails
                }
              }
            }

            // No match found - continue with normal flow
            hideLoader();
            setUploadedFiles(prev =>
              prev.map(f => f.id === fileId ? {
                ...f,
                analysisStatus: 'completed',
                vwbaAnalysis: vwbaAnalysis,
                rawResponse: analysis.rawResponse
              } : f)
            );
            return; // Stop polling
          } else if (analysis.status === 'ERROR') {
            hideLoader();
            setUploadedFiles(prev =>
              prev.map(f => f.id === fileId ? {
                ...f,
                analysisStatus: 'error',
                error: 'Analysis failed'
              } : f)
            );
            return; // Stop polling
          } else if (attempts < maxAttempts) {
            // Continue polling
            setTimeout(poll, pollInterval);
          } else {
            // Timeout
            hideLoader();
            setUploadedFiles(prev =>
              prev.map(f => f.id === fileId ? {
                ...f,
                analysisStatus: 'error',
                error: 'Analysis timed out'
              } : f)
            );
          }
        } else if (attempts < maxAttempts) {
          // Continue polling
          setTimeout(poll, pollInterval);
        } else {
          // Timeout
          hideLoader();
          setUploadedFiles(prev =>
            prev.map(f => f.id === fileId ? {
              ...f,
              analysisStatus: 'error',
              error: 'Analysis timed out'
            } : f)
          );
        }
      } catch (error) {
        if (attempts < maxAttempts) {
          // Continue polling on error
          setTimeout(poll, pollInterval);
        } else {
          hideLoader();
          setUploadedFiles(prev => 
            prev.map(f => f.id === fileId ? { 
              ...f, 
              analysisStatus: 'error',
              error: error instanceof Error ? error.message : 'Analysis failed'
            } : f)
          );
        }
      }
    };

    // Start polling
    setTimeout(poll, pollInterval);
  };

  const removeFile = async (fileId: string) => {
    try {
      // Find the file to get its factSheetId and authenticatedUrl
      const file = uploadedFiles.find(f => f.id === fileId);
      if (file) {
        // Clean up blob URL to prevent memory leaks
        if (file.authenticatedUrl) {
          URL.revokeObjectURL(file.authenticatedUrl);
        }
        
        if (file.factSheetId) {
          // Delete the fact sheet from the backend
          await deleteFactSheet(file.factSheetId);
        }
      }
      
      // Remove the file from the uploaded files list
      setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    } catch (error) {
      console.error('Error deleting file:', error);
      // Still remove from UI even if backend deletion fails
      setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
      alert('File removed from upload list, but there may have been an error deleting it from the server.');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const isSectionCollapsed = (sectionId: string) => {
    return collapsedSections[sectionId] || false;
  };

  const toggleDetails = (fileId: string) => {
    setShowDetails(prev => ({
      ...prev,
      [fileId]: !prev[fileId]
    }));
  };

  const isDetailsShown = (fileId: string) => {
    return showDetails[fileId] || false;
  };

  const saveProject = async (analysis: VWBAAnalysis, fileName: string, fileId: string) => {
    try {
      const projectName = analysis.projectName || `Project from ${fileName}`;
      const projectDescription = analysis.projectDescription || `Analysis results from ${fileName}`;
      const data = analysis.extractedData || {};

      // Use manual location if provided, otherwise use extracted data
      const basin = manualLocations[fileId] || data.basin || data.location || undefined;
      const status = data.status || 'clean';
      const flags = Array.isArray(data.flags) ? data.flags : undefined;

      // Create the project with analysis-derived fields
      const project = await createProject({
        name: projectName,
        description: projectDescription,
        status: status as 'clean' | 'stage1' | 'stage2' | 'non-eligible',
        type: (data.type as 'new' | 'historic') || undefined,
        category: (data.category as 'reforestation' | 'wetland' | 'efficiency' | 'infrastructure') || undefined,
        basin,
        volumetricWaterBenefit: data.volumetricWaterBenefit || data.benefitVolume || undefined,
        costShare: data.costShare || undefined,
        quantificationMethod: data.quantificationMethod || undefined,
        benefitEndDate: data.benefitEndDate || undefined,
        flags,
      });

      // Find the file to get its factSheetId
      const file = uploadedFiles.find(f => f.id === fileId);
      if (file && file.factSheetId) {
        // Associate the fact sheet with the project
        await associateFactSheetWithProject(file.factSheetId, project.id);
      }

      // Remove the file from the uploaded files list after successful save
      setUploadedFiles(prev => prev.filter(f => f.id !== fileId));

      setToast({ message: 'Project saved successfully! It has been moved to the Projects page.', type: 'success' });
    } catch (error) {
      console.error('Error saving project:', error);
      setToast({ message: 'Failed to save project. Please try again.', type: 'error' });
    }
  };

  const handleComparisonResolved = () => {
    if (comparisonModal) {
      // Remove the file from the upload list after resolution
      setUploadedFiles(prev => prev.filter(f => f.id !== comparisonModal.fileId));
      setComparisonModal(null);
      setToast({ message: 'Project updated successfully!', type: 'success' });
    }
  };

  const openComparisonModal = (file: UploadedFile) => {
    if (file.matchedProject && file.vwbaAnalysis && file.factSheetId) {
      setComparisonModal({
        isOpen: true,
        extractedData: file.vwbaAnalysis,
        existingProject: file.matchedProject,
        factSheetId: file.factSheetId,
        fileId: file.id,
      });
    }
  };

  const saveAsMatchedProject = async (file: UploadedFile) => {
    if (!file.matchedProject || !file.vwbaAnalysis || !file.factSheetId) return;
    try {
      const data = file.vwbaAnalysis.extractedData || {};
      const proj = file.matchedProject;

      const previousYearData: { volumetricWaterBenefit: string; costShare?: string } = {
        volumetricWaterBenefit: proj.volumetricWaterBenefit || '',
      };
      if (proj.costShare) {
        previousYearData.costShare = proj.costShare;
      }

      // Build flags for fields that differ between existing project and new factsheet
      const fieldChecks: { field: string; label: string; oldVal: string; newVal: string }[] = [
        { field: 'volumetricWaterBenefit', label: 'Volumetric Water Benefit', oldVal: proj.volumetricWaterBenefit || '', newVal: data.volumetricWaterBenefit || data.benefitVolume || '' },
        { field: 'costShare', label: 'Cost-Share', oldVal: proj.costShare || '', newVal: data.costShare || '' },
        { field: 'basin', label: 'Location/Basin', oldVal: proj.basin || '', newVal: data.basin || data.location || '' },
        { field: 'quantificationMethod', label: 'Quantification Method', oldVal: proj.quantificationMethod || '', newVal: data.quantificationMethod || '' },
        { field: 'benefitEndDate', label: 'Benefit Claim End Date', oldVal: proj.benefitEndDate || '', newVal: data.benefitEndDate || '' },
      ];

      const flags = fieldChecks
        .filter(c => c.newVal && c.oldVal !== c.newVal)
        .map(c => ({
          type: 'System vs Document Inconsistency',
          description: `${c.label} differs between existing project (${c.oldVal || 'not set'}) and new fact sheet (${c.newVal})`,
          field: c.field,
          systemValue: c.oldVal,
          factSheetValue: c.newVal,
          riskLevel: 'high',
        }));

      // Keep old values in project; store differences as stage2 flags for Control Tower review
      await updateProject(proj.id, {
        status: flags.length > 0 ? 'stage2' as 'clean' | 'stage1' | 'stage2' : 'clean',
        hasComparison: true,
        flags,
        previousYearData,
      });

      await associateFactSheetWithProject(file.factSheetId, proj.id);

      setUploadedFiles(prev => prev.filter(f => f.id !== file.id));
      if (flags.length > 0) {
        setToast({ message: 'Project saved with differences flagged for review in Control Tower.', type: 'success' });
      } else {
        setToast({ message: 'Project updated successfully!', type: 'success' });
      }
    } catch (error) {
      console.error('Error saving as matched project:', error);
      setToast({ message: 'Failed to update project. Please try again.', type: 'error' });
    }
  };

  return (
    <div className="h-full w-full px-4 py-4 space-y-4">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-6 right-6 z-[60] flex items-center gap-3 px-5 py-3 rounded-lg shadow-lg transition-all animate-[slideIn_0.3s_ease-out] ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.type === 'success' ? <CheckCircle className="w-5 h-5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
          <span className="text-sm font-medium">{toast.message}</span>
          <button onClick={() => setToast(null)} className="ml-2 p-0.5 hover:bg-white/20 rounded transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* VWBA Analysis Loader */}
      <VWBALoader
        isVisible={showLoader}
        onClose={() => {
          setShowLoader(false);
        }}
      />

      {/* Factsheet Comparison Modal */}
      {comparisonModal?.isOpen && comparisonModal.extractedData && comparisonModal.existingProject && (
        <FactsheetComparisonModal
          extractedData={comparisonModal.extractedData}
          existingProject={comparisonModal.existingProject}
          factSheetId={comparisonModal.factSheetId}
          onClose={() => setComparisonModal(null)}
          onResolved={handleComparisonResolved}
        />
      )}

      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Upload VWBA Fact Sheets
        </h2>
        <p className="text-lg text-gray-600">
          Upload PDF or DOCX fact sheets to analyze and determine the appropriate calculation methodology
        </p>
      </div>


      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragActive
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          type="file"
          multiple
          accept=".pdf,.docx"
          onChange={handleFileSelect}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
        
        <div className="space-y-4">
          <Upload className="w-12 h-12 text-gray-400 mx-auto" />
          <div>
            <p className="text-lg font-medium text-gray-900">
              Drop your PDF or DOCX files here, or click to browse
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Supports PDF and DOCX files up to 50MB
            </p>
          </div>
        </div>
      </div>

      {/* Uploaded Files List */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Uploaded Files</h3>
          <div className="space-y-2">
            {uploadedFiles.map((file) => (
              <div
                key={file.id}
                className="flex items-center justify-between p-4 bg-white rounded-lg border"
              >
                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  {file.status === 'uploading' && (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-sm text-blue-600">{file.progress}%</span>
                    </div>
                  )}
                  
                  {file.status === 'success' && (
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center space-x-2 text-green-600">
                        <CheckCircle className="w-5 h-5" />
                        <span className="text-sm">Uploaded</span>
                      </div>
                      
                      {file.analysisStatus === 'pending' && (
                        <div className="flex items-center space-x-2 text-yellow-600">
                          <Brain className="w-4 h-4" />
                          <span className="text-sm">Queued for analysis</span>
                        </div>
                      )}
                      
                      {file.analysisStatus === 'analyzing' && (
                        <div className="flex items-center space-x-2 text-blue-600">
                          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-sm">Analyzing...</span>
                        </div>
                      )}
                      
                      {file.analysisStatus === 'completed' && (
                        <div className="flex items-center space-x-2 text-green-600">
                          <Brain className="w-4 h-4" />
                          <span className="text-sm">Analysis complete</span>
                        </div>
                      )}
                      
                      {file.analysisStatus === 'error' && (
                        <div className="flex items-center space-x-2 text-red-600">
                          <AlertCircle className="w-4 h-4" />
                          <span className="text-sm">Analysis failed</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {file.status === 'error' && (
                    <div className="flex items-center space-x-2 text-red-600">
                      <AlertCircle className="w-5 h-5" />
                      <span className="text-sm">{file.error}</span>
                    </div>
                  )}
                  
                  <button
                    onClick={() => removeFile(file.id)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analysis Results */}
      {uploadedFiles.some(f => f.analysisStatus === 'completed' && f.vwbaAnalysis) && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Analysis Results</h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('structured')}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  viewMode === 'structured'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Structured Data
              </button>
              <button
                onClick={() => setViewMode('raw')}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  viewMode === 'raw'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Raw Response
              </button>
            </div>
          </div>
          {uploadedFiles
            .filter(f => f.analysisStatus === 'completed' && f.vwbaAnalysis)
            .map((file) => (
              <div key={file.id} className="bg-white rounded-lg border">
                {/* PDF Preview and Analysis Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
                  {/* Left Column - PDF Preview */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-md font-medium text-gray-900">Document Preview</h4>
                      <span className="text-sm text-gray-500">{file.name}</span>
                    </div>
                    <div className="border rounded-lg overflow-hidden" style={{ height: '800px' }}>
                      {file.name.toLowerCase().endsWith('.docx') && file.filename ? (
                        <DocxPreview
                          filename={file.filename}
                          title={`Document Preview: ${file.name}`}
                        />
                      ) : file.authenticatedUrl ? (
                        <iframe
                          src={file.authenticatedUrl}
                          className="w-full h-full"
                          title={`Document Preview: ${file.name}`}
                          style={{ border: 'none' }}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full bg-gray-100">
                          <div className="text-center">
                            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-500">Loading preview...</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Right Column - Analysis Data */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-md font-medium text-gray-900">Analysis Data</h4>
                      <button 
                        onClick={() => toggleDetails(file.id)}
                        className="text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                      >
                        <Eye className="w-4 h-4" />
                        <span className="text-sm">
                          {isDetailsShown(file.id) ? 'Hide Details' : 'View Details'}
                        </span>
                      </button>
                    </div>
                
                {/* Compact Summary View - shown by default */}
                {viewMode === 'structured' && file.vwbaAnalysis && !isDetailsShown(file.id) && (
                  <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-4 border border-gray-200">
                    {/* Project Info */}
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <h6 className="font-semibold text-gray-800 text-sm flex items-center mb-2">
                        <FileText className="w-4 h-4 mr-1" />
                        Project
                      </h6>
                      <p className="text-sm font-medium text-gray-900 mb-1">{file.vwbaAnalysis.projectName}</p>
                      <p className="text-xs text-gray-600 line-clamp-2">{file.vwbaAnalysis.projectDescription}</p>
                    </div>

                    {/* Flags Display */}
                    {Array.isArray(file.vwbaAnalysis.extractedData?.flags) && file.vwbaAnalysis.extractedData.flags.length > 0 && (
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <h6 className="text-sm font-semibold text-amber-800 flex items-center gap-1 mb-2">
                          <AlertCircle className="w-4 h-4" />
                          Validation Flags
                        </h6>
                        {file.vwbaAnalysis.extractedData.flags.map((flag: { type: string; description: string }, idx: number) => (
                          <div key={idx} className="text-xs text-amber-700 mb-1">
                            <span className="font-medium">{flag.type}:</span> {flag.description}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Missing Location Prompt */}
                    {!file.vwbaAnalysis.extractedData?.location && !file.vwbaAnalysis.extractedData?.basin && (
                      <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <MapPin className="w-4 h-4 text-orange-600" />
                          <span className="text-sm font-semibold text-orange-800">Location not found in document</span>
                        </div>
                        <p className="text-xs text-orange-600 mb-2">
                          Enter the project location below, or save without it (project will be flagged).
                        </p>
                        <input
                          type="text"
                          value={manualLocations[file.id] || ''}
                          onChange={(e) => setManualLocations(prev => ({ ...prev, [file.id]: e.target.value }))}
                          placeholder="e.g., Pandaan District, East Java, Indonesia"
                          className="w-full px-3 py-1.5 text-sm border border-orange-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
                        />
                      </div>
                    )}

                    {/* Save Project / Match Actions */}
                    <div className="mt-4 pt-3 border-t border-gray-200">
                      {file.matchedProject ? (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 p-3 bg-cyan-50 border border-cyan-200 rounded-lg">
                            <CheckCircle className="w-5 h-5 text-cyan-600 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-cyan-800">
                                Matches existing project
                              </p>
                              <p className="text-xs text-cyan-600 truncate">
                                {file.matchedProject.name}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => saveAsMatchedProject(file)}
                              className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                            >
                              <FileText className="w-4 h-4" />
                              <span>Save as Update</span>
                            </button>
                            <button
                              onClick={() => openComparisonModal(file)}
                              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                            >
                              <Eye className="w-4 h-4" />
                              <span>Compare</span>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => saveProject(file.vwbaAnalysis!, file.name, file.id)}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                        >
                          <FileText className="w-4 h-4" />
                          <span>Save Project</span>
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Detailed View - shown when View Details is clicked */}
                {viewMode === 'structured' && file.vwbaAnalysis && isDetailsShown(file.id) && (
                  <div className="space-y-6">
                    {/* Project Information */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <button
                        onClick={() => toggleSection(`${file.id}-project-info`)}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <h5 className="font-semibold text-gray-800 flex items-center">
                          <FileText className="w-5 h-5 mr-2" />
                          Project Information
                        </h5>
                        {isSectionCollapsed(`${file.id}-project-info`) ? (
                          <ChevronRight className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        )}
                      </button>
                      {!isSectionCollapsed(`${file.id}-project-info`) && (
                      <div className="space-y-2">
                        <div>
                          <span className="font-medium text-gray-700">Project:</span>
                          <p className="text-sm text-gray-600 mt-1">{file.vwbaAnalysis.projectName}</p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Description:</span>
                          <p className="text-sm text-gray-600 mt-1">{file.vwbaAnalysis.projectDescription}</p>
                        </div>
                        <div className="flex items-center space-x-4">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Match: {file.vwbaAnalysis.methodologyMatchScore}%
                          </span>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Data: {file.vwbaAnalysis.dataCompleteness}%
                          </span>
                        </div>
                      </div>
                      )}
                    </div>

                    {/* Recommended Methodology */}
                    <div className="bg-green-50 rounded-lg p-4">
                      <button
                        onClick={() => toggleSection(`${file.id}-recommended-methodology`)}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <h5 className="font-semibold text-gray-800 flex items-center">
                          <Brain className="w-5 h-5 mr-2 text-green-600" />
                          Recommended Methodology
                        </h5>
                        {isSectionCollapsed(`${file.id}-recommended-methodology`) ? (
                          <ChevronRight className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        )}
                      </button>
                      {!isSectionCollapsed(`${file.id}-recommended-methodology`) && (
                      <div className="space-y-3">
                        <div>
                          <span className="font-medium text-gray-700">Methodology:</span>
                          <p className="text-sm text-gray-600 mt-1">{file.vwbaAnalysis.recommendedMethodology?.name}</p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Description:</span>
                          <p className="text-sm text-gray-600 mt-1">{file.vwbaAnalysis.recommendedMethodology?.description}</p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Confidence:</span>
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {file.vwbaAnalysis.recommendedMethodology?.methodologyMatchScore}%
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Reasoning:</span>
                          <p className="text-sm text-gray-600 mt-1">{file.vwbaAnalysis.recommendedMethodology?.reasoning}</p>
                        </div>
                        
                        {/* Methodology Inputs */}
                        <div>
                          <span className="font-medium text-gray-700">Required Inputs:</span>
                          <div className="mt-2 space-y-2">
                            {file.vwbaAnalysis.recommendedMethodology?.inputs.map((input, index) => (
                              <div key={index} className="bg-white rounded p-3 border">
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-sm text-gray-700">{input.name}</span>
                                  <div className="flex items-center space-x-2">
                                    {input.available ? (
                                      <CheckCircle className="w-4 h-4 text-green-500" />
                                    ) : (
                                      <AlertCircle className="w-4 h-4 text-red-500" />
                                    )}
                                    <span className={`text-xs px-2 py-1 rounded ${
                                      input.required ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                      {input.required ? 'Required' : 'Optional'}
                                    </span>
                                  </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">{input.description}</p>
                                {input.value && (
                                  <p className="text-sm text-gray-600 mt-1">
                                    <span className="font-medium">Value:</span> {input.value} {input.unit && `(${input.unit})`}
                                  </p>
                                )}
                                {input.source && (
                                  <p className="text-xs text-gray-400 mt-1">
                                    <span className="font-medium">Source:</span> {input.source}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Calculation Steps */}
                        <div>
                          <span className="font-medium text-gray-700">Calculation Steps:</span>
                          <ol className="mt-2 space-y-1">
                            {file.vwbaAnalysis.recommendedMethodology?.calculationSteps.map((step, index) => (
                              <li key={index} className="text-sm text-gray-600 flex">
                                <span className="font-medium mr-2">{index + 1}.</span>
                                <span>{step}</span>
                              </li>
                            ))}
                          </ol>
                        </div>

                        {/* References */}
                        <div>
                          <span className="font-medium text-gray-700">References:</span>
                          <ul className="mt-2 space-y-1">
                            {file.vwbaAnalysis.recommendedMethodology?.references.map((ref, index) => (
                              <li key={index} className="text-sm text-gray-600 flex">
                                <span className="mr-2">•</span>
                                <span>{ref}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      )}
                    </div>

                    {/* Other Applicable VWBA Methods */}
                    {file.vwbaAnalysis.otherApplicableMethods && file.vwbaAnalysis.otherApplicableMethods.length > 0 && (
                      <div className="bg-yellow-50 rounded-lg p-4">
                        <button
                          onClick={() => toggleSection(`${file.id}-alternative-methodologies`)}
                          className="w-full flex items-center justify-between text-left"
                        >
                          <h5 className="font-semibold text-gray-800 flex items-center">
                            <AlertCircle className="w-5 h-5 mr-2 text-yellow-600" />
                            Other Applicable VWBA Methods
                          </h5>
                          {isSectionCollapsed(`${file.id}-alternative-methodologies`) ? (
                            <ChevronRight className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          )}
                        </button>
                        {!isSectionCollapsed(`${file.id}-alternative-methodologies`) && (
                        <div className="space-y-4">
                          {file.vwbaAnalysis.otherApplicableMethods.map((methodology, index) => (
                            <div key={index} className="bg-white rounded p-3 border">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-gray-700">{methodology.name}</span>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  {methodology.methodologyMatchScore}% confidence
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 mb-2">{methodology.description}</p>
                              <p className="text-xs text-gray-500">{methodology.reasoning}</p>
                            </div>
                          ))}
                        </div>
                        )}
                      </div>
                    )}

                    {/* Extracted Data */}
                    {file.vwbaAnalysis.extractedData && (
                      <div className="bg-blue-50 rounded-lg p-4">
                        <button
                          onClick={() => toggleSection(`${file.id}-extracted-data`)}
                          className="w-full flex items-center justify-between text-left"
                        >
                          <h5 className="font-semibold text-gray-800 flex items-center">
                            <FileText className="w-5 h-5 mr-2 text-blue-600" />
                            Extracted Data
                          </h5>
                          {isSectionCollapsed(`${file.id}-extracted-data`) ? (
                            <ChevronRight className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          )}
                        </button>
                        {!isSectionCollapsed(`${file.id}-extracted-data`) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {Object.entries(file.vwbaAnalysis.extractedData).map(([key, value]) => (
                            <div key={key} className="bg-white rounded p-3 border">
                              <span className="font-medium text-gray-700 capitalize">
                                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                              </span>
                              <div className="mt-1">
                                {typeof value === 'object' && value !== null ? (
                                  <div className="space-y-1">
                                    {Object.entries(value).map(([subKey, subValue]) => (
                                      <div key={subKey} className="text-sm">
                                        <span className="font-medium text-gray-600">
                                          {subKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                                        </span>
                                        <span className="text-gray-600 ml-1">
                                          {Array.isArray(subValue) ? subValue.join(', ') : String(subValue)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-600">{String(value)}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        )}
                      </div>
                    )}

                    {/* Available and Missing Inputs */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Available Inputs */}
                      <div className="bg-green-50 rounded-lg p-4">
                        <button
                          onClick={() => toggleSection(`${file.id}-available-inputs`)}
                          className="w-full flex items-center justify-between text-left"
                        >
                          <h5 className="font-semibold text-gray-800 flex items-center">
                            <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                            Available Inputs ({file.vwbaAnalysis.availableInputs.length})
                          </h5>
                          {isSectionCollapsed(`${file.id}-available-inputs`) ? (
                            <ChevronRight className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          )}
                        </button>
                        {!isSectionCollapsed(`${file.id}-available-inputs`) && (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {file.vwbaAnalysis.availableInputs.map((input, index) => (
                            <div key={index} className="bg-white rounded p-3 border">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-sm text-gray-700">{input.name}</span>
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              </div>
                              <p className="text-xs text-gray-500 mt-1">{input.description}</p>
                              {input.value && (
                                <p className="text-sm text-gray-600 mt-1">
                                  <span className="font-medium">Value:</span> {input.value} {input.unit && `(${input.unit})`}
                                </p>
                              )}
                              {input.source && (
                                <p className="text-xs text-gray-400 mt-1">
                                  <span className="font-medium">Source:</span> {input.source}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                        )}
                      </div>

                      {/* VWBA Requirements */}
                      <div className="bg-red-50 rounded-lg p-4">
                        <button
                          onClick={() => toggleSection(`${file.id}-missing-inputs`)}
                          className="w-full flex items-center justify-between text-left"
                        >
                          <h5 className="font-semibold text-gray-800 flex items-center">
                            <AlertCircle className="w-5 h-5 mr-2 text-red-600" />
                            VWBA Requirements ({file.vwbaAnalysis.vwbaRequirements.length})
                          </h5>
                          {isSectionCollapsed(`${file.id}-missing-inputs`) ? (
                            <ChevronRight className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          )}
                        </button>
                        {!isSectionCollapsed(`${file.id}-missing-inputs`) && (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {file.vwbaAnalysis.vwbaRequirements.map((input, index) => (
                            <div key={index} className="bg-white rounded p-3 border">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-sm text-gray-700">{input.name}</span>
                                <div className="flex items-center space-x-2">
                                  <AlertCircle className="w-4 h-4 text-red-500" />
                                  <span className={`text-xs px-2 py-1 rounded ${
                                    input.required ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {input.required ? 'Required' : 'Optional'}
                                  </span>
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">{input.description}</p>
                              {input.unit && (
                                <p className="text-xs text-gray-400 mt-1">
                                  <span className="font-medium">Unit:</span> {input.unit}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                        )}
                      </div>
                    </div>

                    {/* Processing Notes */}
                    {file.vwbaAnalysis.processingNotes && file.vwbaAnalysis.processingNotes.length > 0 && (
                      <div className="bg-purple-50 rounded-lg p-4">
                        <button
                          onClick={() => toggleSection(`${file.id}-processing-notes`)}
                          className="w-full flex items-center justify-between text-left"
                        >
                          <h5 className="font-semibold text-gray-800 flex items-center">
                            <Brain className="w-5 h-5 mr-2 text-purple-600" />
                            Processing Notes
                          </h5>
                          {isSectionCollapsed(`${file.id}-processing-notes`) ? (
                            <ChevronRight className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          )}
                        </button>
                        {!isSectionCollapsed(`${file.id}-processing-notes`) && (
                        <div className="space-y-2">
                          {file.vwbaAnalysis.processingNotes.map((note, index) => (
                            <div key={index} className="bg-white rounded p-3 border">
                              <p className="text-sm text-gray-600">{note}</p>
                            </div>
                          ))}
                        </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {viewMode === 'raw' && file.rawResponse && (
                  <div className="space-y-4">
                    <h5 className="font-medium text-gray-700 mb-2">Raw Gemini Response</h5>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <pre className="text-sm text-gray-800 whitespace-pre-wrap overflow-x-auto">
                        {file.rawResponse}
                      </pre>
                    </div>
                  </div>
                )}
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default UploadPage;
